import { FC, FormEvent } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import { FormLayout } from "@/components/Forms";
import Button from "@/components/Button";
import {
  MinimalChannelResource,
  ProductionProjectResource,
} from "@/types/resources";
import { api } from "@/utils/api";
import useAlerts from "@/hooks/useAlerts";
import { handleFormErrors } from "@/utils/helpers";
import FormField from "@/components/Forms/FormField";
import ProjectTopicsField from "./ProjectTopicsField";

export type FormDataT = {
  name: string;
  owner: string;
  director?: string;
  channel_id: number;
  external_id: string;
  inspiration: string;
  topics: string[];
};

interface Props {
  onClose: () => void;
  project?: ProductionProjectResource;
}

const ProjectModal: FC<Props> = ({ onClose, project }) => {
  const { teamId } = useParams();

  const { data: channels } = useQuery(
    ["channels", { teamId, authorized: true }],
    () =>
      api.get<never, MinimalChannelResource[]>(`/v1/channels/authorized`, {
        params: {
          ...(teamId !== "all" && {
            team_id: teamId,
          }),
        },
      }),
    {
      staleTime: 120 * 1000,
      select: (data) =>
        data.map((channel) => {
          return {
            label: channel.name,
            value: channel.id,
          };
        }),
    }
  );

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      name: project?.name ?? "",
      owner: project?.owner ?? "",
      director: project?.director ?? "",
      channel_id: project?.channel.id,
      external_id: project?.externalId ?? "",
      inspiration: project?.inspiration ?? "",
      topics: project?.topics?.map((topic) => topic.title) ?? [],
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        const body = {
          ...data,
          team_id: teamId,
        };

        if (project) {
          await api.put<never, { id: number }>(
            `/v1/production/projects/${project.id}`,
            body
          );

          queryClient.invalidateQueries(["production_projects"]);

          alerts.success(`Project has been successfully updated`);

          onClose();
        } else {
          const { id } = await api.post<never, { id: number }>(
            "/v1/production/projects",
            body
          );

          navigate(id.toString());

          alerts.success(`Project has been successfully created`);
        }
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel heading="New Project" onClose={onClose}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                name="name"
                control={control}
                label="Project name"
              />
              <FormField<FormDataT>
                name="owner"
                control={control}
                label="Project owner"
              />
              <FormField<FormDataT>
                name="director"
                control={control}
                label="Director"
              />
              <FormField<FormDataT, number>
                name="channel_id"
                control={control}
                label="Channel"
                type="select"
                options={channels || []}
              />
              <FormField<FormDataT>
                name="external_id"
                control={control}
                label="Project ID"
              />
              <ProjectTopicsField control={control} />
              <FormField<FormDataT>
                name="inspiration"
                control={control}
                label="Inspiration"
                type="textarea"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Save
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default ProjectModal;
